/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import ReceiverPayoutHistory from "./ReceiverPayoutHistory";
import { usePageWebSocket } from "../../../../hooks/usePageWebSocket";

const rowPerPage = 10;

interface PayloadType {
  [key: string]: any;
}

const ReceiverPayoutHistoryTab: React.FC<{
  detail: any;
}> = ({ detail }) => {

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState([]);
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const pageRef = useRef(page);
  const currentLastKeyRef = useRef(currentLastKey);
  pageRef.current = page;
  currentLastKeyRef.current = currentLastKey;

  const { received } = usePageWebSocket("payout_list");

  useEffect(() => {
    const fetchData = async () => {
      await fetchPayouts({ page: 0, limit: rowPerPage, lastkey: "", receiver_id: detail.id });
    };

    if(detail !== null) {
      fetchData();
    }
  }, [detail]);

  // Listen for WebSocket messages
  useEffect(() => {
    if (!received) return;

    // Handle individual payout cancel — update row status directly
    if (received.messageType === "payout") {
      const payoutId = received.data?.payout_id;
      const newStatus = received.data?.status;
      if (payoutId && newStatus) {
        setTableData((prev: any) => prev.map((row: any) =>
          row.id === payoutId ? { ...row, status: newStatus } : row
        ));
      }
      return;
    }

    // Silent reload on SNS (batch complete)
    if (received.messageType !== "batch") return;
    if (received.data?.processing) return; // ignore processing-started messages

    fetchPayouts({
      page: pageRef.current,
      limit: rowPerPage,
      lastkey: pageRef.current !== 0 ? currentLastKeyRef.current : "",
      receiver_id: detail.id,
    });
  }, [received]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPaginationModel = (newValue: any) => {
    setIsLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      receiver_id: detail.id
    };

    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        receiver_id: detail.id
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys([]);
    } else {
      setPage(newValue.page);
    }

    fetchPayouts(newFilters);
  };

  const fetchPayouts = async (payload: PayloadType = {}) => {
    try {
      const queryObject: PayloadType = {
        limit: payload?.limit,
        lastkey: payload?.lastkey,
        receiver_id: payload?.receiver_id,
        usertype: userRole === 'superadmin' ? "a" : "m"
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/payout/get-all-payouts?${queryString}`
      );

      let newData = response.data.payouts?.payload;

      if (newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }
      setIsLoading(false);

      let newLastKey = response.data.payouts?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        let newPage = payload?.page;
        let newPageSize = payload?.limit;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount((newPage) * newPageSize + newPageSize);
        }
        setLastKeys({ ...lastKeys, [newPage]: newLastKey });
      } else {
        setCurrentLastKey("");
      }
    } catch (error) {
      setTableData([]);
      setIsLoading(false);
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      setLastKeys([]);
      setCurrentLastKey("");
      console.error("Error fetching data", error);
    }
  };

  return (
    <div>
      <ReceiverPayoutHistory
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        tableData={isLoading ? [] : tableData}
        setPaginationModel={setPaginationModel}
        currentLastKey={currentLastKey}
      />
    </div>
  );
};

export default ReceiverPayoutHistoryTab;
